import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  fetchSinglePart,
  savePart,
  removeSavedPart,
  fetchSavedParts,
  fetchSellerProfile,
} from "../store/parts/partsAction";
import {
  fetchMessages,
  fetchSellerById,
  updateIsRead,
} from "../store/partChat/partChatActions";
import { openAuthDialog } from "../store/auth/authSlice";
import { useTheme } from "../components/ThemeProvider";
import PartChatDialog from "../components/PartChatDialog";
import PartPaymentModal from "../components/Modal/PartPaymentModal";
import { toast } from "../components/ui/use-toast";
import {
  ShoppingCart,
  MessageSquare,
  Bookmark,
  Package,
  Factory,
  Truck,
  Calendar,
  Layers,
  MapPin,
  Building,
  Star,
  Zap,
  Tag,
  ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
// import { Separator } from "@/components/ui/separator";
import PartImageSlider from "../hooks/partImageSlider";
import StarRating from "@/components/ui/StarRating";

const PartDetailPage = () => {
  const { partId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    singlePart: part,
    saveLoading,
    partLoading,
    removeLoading,
    savedParts,
  } = useSelector((state) => state.parts);

  const { messages, isMessagesLoading, isSenderLoading } = useSelector(
    (state) => state.partChat,
  );
  const { user, isAuthenticated, currentSubscription } = useSelector(
    (state) => state.auth,
  );
  const { theme } = useTheme();
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [sellerProfile, setSellerProfile] = useState(null);

  useEffect(() => {
    if (partId) dispatch(fetchSinglePart(partId));
  }, [dispatch, partId]);

  useEffect(() => {
    const fetchProfile = async () => {
      if (part?.sellerId) {
        try {
          const profile = await dispatch(
            fetchSellerProfile(part.sellerId),
          ).unwrap();
          if (profile) setSellerProfile(profile);
        } catch (err) {
          console.error("Failed to fetch dynamic seller profile", err);
        }
      }
    };

    fetchProfile();
  }, [dispatch, part?.sellerId]);

  useEffect(() => {
    if (user && isAuthenticated) {
      dispatch(fetchSavedParts({ userId: user?.uid }));
    }
  }, [user, isAuthenticated, dispatch]);

  if (partLoading)
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading part details...</p>
        </div>
      </div>
    );

  if (!part)
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <div className="text-center">
          <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground text-lg">Part not found.</p>
          <Button
            onClick={() => navigate(-1)}
            className="mt-4"
            variant="outline"
          >
            Go Back
          </Button>
        </div>
      </div>
    );

  const isSaved = savedParts?.some(
    (saved) => saved.partId === part.id && saved.userId === user?.uid,
  );

  const handleSaveClick = () => {
    if (!isAuthenticated || !user) {
      toast({
        title: "Login Required",
        description: "Please login to save parts.",
        variant: "destructive",
      });
      dispatch(
        openAuthDialog({
          showLoginFirstOrOnSuccess: true,
          redirectPage: window.location.pathname,
        }),
      );
      return;
    }
    isSaved
      ? dispatch(removeSavedPart({ userId: user.uid, partId: part.id }))
      : dispatch(savePart({ userId: user.uid, part }));
  };

  const handleChatClick = async () => {
    if (!isAuthenticated || !user) {
      toast({
        title: "Login Required",
        description: "Please login to send a message to Seller.",
        variant: "destructive",
      });
      dispatch(
        openAuthDialog({
          showLoginFirstOrOnSuccess: true,
          redirectPage: window.location.pathname,
        }),
      );
      return;
    }

    try {
      const sellerData = await dispatch(
        fetchSellerById({ userId: part?.sellerId }),
      ).unwrap();

      const conversation = {
        partInfo: part,
        otherUserDetail: sellerData,
      };

      dispatch(
        fetchMessages({
          buyerId: user?.uid,
          sellerId: part?.sellerId,
          partId: part?.id,
          currentUserId: user?.uid,
        }),
      );

      setTimeout(() => {
        dispatch(
          updateIsRead({
            currentUserId: user?.uid,
            receiverId: part?.sellerId,
            partId: part?.id,
          }),
        );
      }, 1000);

      setSelectedConversation(conversation);
    } catch (error) {
      console.error("❌ Failed to fetch seller:", error);
      toast({
        title: "Error",
        description: "Failed to open chat. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleBuyClick = () => {
    if (!isAuthenticated || !user) {
      toast({
        title: "Login Required",
        description: "Please login as buyer to buy Parts.",
        variant: "destructive",
      });
      dispatch(
        openAuthDialog({
          showLoginFirstOrOnSuccess: true,
          redirectPage: window.location.pathname,
        }),
      );
      return;
    }

    if (!part?.stripePaymentLink || part?.stripePaymentLink === "") {
      toast({
        title: "Payment Unavailable",
        description: "This seller has not added a Stripe payment link.",
        variant: "destructive",
      });
      return;
    }
    setShowPaymentModal(true);
  };

  const handleSellerClick = () => {
    const slug = part?.sellerName;
    const isPremium = currentSubscription?.planId === "premium";
    if (slug && isPremium) {
      const displaySlug = slug.replace(/\s+/g, "-").toLowerCase();
      navigate(`/${encodeURIComponent(displaySlug)}`);
    } else {
      toast({
        title: "Premium Required",
        description: "Please upgrade to premium to view seller details.",
        variant: "destructive",
      });
    }
  };

  const tierStyles = {
    featured: {
      icon: Tag,
      label: "Featured",
      bgColor: "bg-yellow-100 dark:bg-yellow-100",
      textColor: "text-yellow-700 dark:text-yellow-800",
      borderColor: "border-yellow-300 dark:border-yellow-800",
    },
  };

  const getCurrentTier = (part) => {
    if (part?.isFeatured) return tierStyles.featured;
    return null;
  };

  const tier = getCurrentTier();

  const displayPrice =
    typeof part?.price === "number"
      ? part?.price.toFixed(2)
      : typeof part?.price === "string" && !isNaN(parseFloat(part?.price))
        ? parseFloat(part?.price).toFixed(2)
        : "N/A";

  const bgClass =
    theme === "dark"
      ? "bg-gray-950"
      : theme === "light"
        ? "bg-gray-50"
        : "bg-background";

  return (
    <div className={`min-h-screen ${bgClass} transition-colors duration-300`}>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <div className="mb-6 text-sm text-muted-foreground">
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            size="lg"
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </Button>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column - Images */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card className="overflow-hidden border-2 shadow-lg">
              <div className="relative aspect-[4/3] bg-secondary">
                <PartImageSlider
                  images={
                    part?.imageUrls?.length
                      ? part.imageUrls
                      : part?.imageReferences?.length
                        ? part.imageReferences
                        : part?.imageUrl
                          ? [part.imageUrl]
                          : []
                  }
                  alt={`Image of ${part?.name} for ${part?.model} ${part?.year}`}
                />
                {tier && (
                  <span
                    className={`absolute top-4 right-4 inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold border-2 ${tier.bgColor} ${tier.textColor} ${tier.borderColor} shadow-lg`}
                  >
                    <tier.icon className="mr-1.5 h-4 w-4" />
                    {tier.label}
                  </span>
                )}
                {part?.isFounderMember && (
                  <span
                    className={`absolute top-1.5 left-1.5 inline-flex items-center px-3 py-1.5 rounded-full text-sm text-white font-semibold border border-yellow-100 bg-yellow-600`}
                  >
                    Founding Seller
                  </span>
                )}
              </div>
            </Card>
          </motion.div>

          {/* Right Column - Details */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            {/* Header */}
            <div className="space-y-4">
              {/* Header Section */}
              <div className="flex justify-between items-start flex-wrap gap-3">
                <h1 className="text-3xl lg:text-4xl font-bold tracking-tight text-foreground leading-tight">
                  {part.name == "other" ? part?.sub_name : part?.name}
                </h1>

                {/* Bookmark */}
                {user?.role === "buyer" && isAuthenticated && (
                  <button
                    onClick={handleSaveClick}
                    disabled={saveLoading || removeLoading}
                    className="relative group p-2 rounded-full bg-background border hover:border-primary/50 transition-all hover:shadow-sm"
                  >
                    {isSaved ? (
                      <Bookmark className="w-6 h-6 text-primary fill-primary transition-transform duration-300 group-hover:scale-110" />
                    ) : (
                      <Bookmark className="w-6 h-6 text-muted-foreground transition-colors duration-300 group-hover:text-primary group-hover:scale-110" />
                    )}
                  </button>
                )}
              </div>

              {/* Badges Section */}
              <div className="flex flex-wrap gap-2">
                {part.tier && (
                  <Badge
                    variant="outline"
                    className="px-3 py-1.5 bg-gradient-to-r from-indigo-100 to-indigo-200 text-indigo-800 dark:from-indigo-900/40 dark:to-indigo-900/20 dark:text-indigo-300 border border-indigo-300/50"
                  >
                    {part.tier}
                  </Badge>
                )}

                <Badge
                  variant="outline"
                  className={`px-3 py-1.5 ${
                    part.status === "Low Stock"
                      ? "bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-800 dark:from-yellow-900/40 dark:to-yellow-900/20 dark:text-yellow-300 border-yellow-300/50"
                      : "bg-gradient-to-r from-emerald-100 to-emerald-200 text-emerald-800 dark:from-emerald-900/40 dark:to-emerald-900/20 dark:text-emerald-300 border-emerald-300/50"
                  }`}
                >
                  {part.status}
                </Badge>

                <Badge
                  variant="outline"
                  className="px-3 py-1.5 bg-gray-100 text-gray-800 dark:bg-gray-800/40 dark:text-gray-300 border-gray-300/50"
                >
                  Qty: {part.quantity}
                </Badge>
              </div>

              {/* Price Section */}
              <div className="flex items-baseline gap-2 pt-2">
                <span className="text-5xl font-extrabold text-primary tracking-tight drop-shadow-sm">
                  ${displayPrice}
                </span>
                <span className="text-muted-foreground text-sm font-medium mt-2">
                  USD
                </span>
              </div>
            </div>

            {/* Specifications Card */}
            <Card
              className="bg-gradient-to-br from-white via-gray-50 to-gray-100 
  dark:from-gray-900 dark:via-gray-950 dark:to-gray-900 
  border border-gray-200 dark:border-gray-800 
  rounded-2xl shadow-sm hover:shadow-md transition-all duration-300"
            >
              <div className="p-6">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-1.5 h-6 bg-primary rounded-full" />
                  <h3 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                    Specifications
                  </h3>
                </div>

                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-5">
                  {/* Make */}
                  <div className="flex items-start gap-3 bg-gray-50 dark:bg-gray-800/40 p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                    <Factory className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">
                        Make
                      </p>
                      <p className="font-medium text-gray-800 dark:text-gray-100">
                        {part.make == "other"
                          ? part?.sub_make
                          : part?.make || "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Model */}
                  <div className="flex items-start gap-3 bg-gray-50 dark:bg-gray-800/40 p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                    <Truck className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">
                        Model
                      </p>
                      <p className="font-medium text-gray-800 dark:text-gray-100">
                        {part.model == "other"
                          ? part?.sub_model
                          : part?.model || "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Year */}
                  <div className="flex items-start gap-3 bg-gray-50 dark:bg-gray-800/40 p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                    <Calendar className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">
                        Year
                      </p>
                      <p className="font-medium text-gray-800 dark:text-gray-100">
                        {part.year || "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Tier (if available) */}
                  {part.tier && (
                    <div className="flex items-start gap-3 bg-gray-50 dark:bg-gray-800/40 p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                      <Layers className="h-5 w-5 text-primary mt-0.5" />
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">
                          Tier
                        </p>
                        <p className="font-medium text-gray-800 dark:text-gray-100">
                          {part.tier}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Card>

            {/* Description */}
            {part.description && (
              <Card
                className="bg-gradient-to-br from-white via-gray-50 to-gray-100 
    dark:from-gray-900 dark:via-gray-950 dark:to-gray-900 
    border border-gray-200 dark:border-gray-800 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300"
              >
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-1.5 h-6 bg-primary rounded-full" />
                    <h3 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                      Description
                    </h3>
                  </div>
                  <p className="text-base leading-relaxed text-gray-600 dark:text-gray-300">
                    {part.description}
                  </p>
                </div>
              </Card>
            )}

            {/* Seller Card */}
            <Card className="bg-card border">
              <div className="p-5">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Building className="h-5 w-5 text-primary" />
                  Seller Information
                </h3>
                <div
                  className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 p-5 rounded-2xl 
  bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-950 dark:to-gray-900 
  shadow-sm border border-gray-200 dark:border-gray-800 transition-all duration-300"
                >
                  {/* Left: Seller Identity */}
                  <div className="flex items-center gap-4">
                    <div
                      onClick={handleSellerClick}
                      className="h-14 w-14 rounded-full overflow-hidden border border-gray-200 dark:border-gray-800 shadow-sm bg-white dark:bg-gray-950 flex items-center justify-center shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                    >
                      {sellerProfile?.logoUrl || part.sellerLogo ? (
                        <img
                          src={sellerProfile?.logoUrl || part.sellerLogo}
                          alt={
                            sellerProfile?.companyName ||
                            sellerProfile?.sellerName ||
                            part.sellerName
                          }
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <Building className="h-6 w-6 text-muted-foreground/50" />
                      )}
                    </div>
                    <div>
                      <h3
                        onClick={handleSellerClick}
                        className="text-xl font-semibold leading-tight cursor-pointer hover:text-primary hover:underline transition-colors"
                      >
                        {sellerProfile?.companyName ||
                          sellerProfile?.sellerName ||
                          part.sellerName}
                      </h3>
                      {(sellerProfile?.postalCode || part.postalCode) && (
                        <div className="flex items-center gap-1.5 mt-1 text-sm text-muted-foreground">
                          <MapPin className="h-4 w-4 text-primary/80" />
                          <span>
                            {sellerProfile?.postalCode || part.postalCode}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {user?.role === "seller" &&
                  user?.uid === part?.sellerId ? null : (
                    <Button
                      onClick={handleChatClick}
                      variant="outline"
                      size="lg"
                      className="gap-2"
                    >
                      <MessageSquare className="h-5 w-5 group-hover:animate-pulse" />
                      Chat
                    </Button>
                  )}
                </div>
              </div>
            </Card>

            {/* Action Buttons */}
            {user?.role !== "seller" && (
              <div className="flex flex-col justify-end sm:flex-row gap-3 pt-4">
                {part?.stripePaymentLink && part?.stripePaymentLink !== "" && (
                  <Button
                    onClick={handleBuyClick}
                    variant="default"
                    size="lg"
                    className="gap-2"
                  >
                    <ShoppingCart className="h-5 w-5 transition-transform duration-300 group-hover:scale-110" />
                    Buy Now
                  </Button>
                )}
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Chat Dialog */}
      {selectedConversation && (
        <PartChatDialog
          messages={messages}
          conversation={selectedConversation}
          isOpen={!!selectedConversation}
          isMessagesLoading={isMessagesLoading}
          isSenderLoading={isSenderLoading}
          onOpenChange={(open) => {
            if (!open) setSelectedConversation(null);
          }}
        >
          <></>
        </PartChatDialog>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <PartPaymentModal
          open={showPaymentModal}
          part={part}
          close={() => setShowPaymentModal(false)}
        />
      )}
    </div>
  );
};

export default PartDetailPage;
